/**
 * Shared utilities for product attribute handling
 */

/**
 * Natural sort comparator for strings
 * Handles mixed numeric/text values correctly (e.g., "2" before "13")
 */
export function naturalSort(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * Computes a numeric order for standard letter sizes (XS, S, M, L, XL, XXL, 2XL, etc.)
 * Returns null if the value doesn't match a known size pattern
 *
 * Order scale:
 * - XXXS = -2, XXS = -1, XS = 0, S = 1, M = 2, L = 3, XL = 4, XXL/2XL = 5, XXXL/3XL = 6, etc.
 */
function getSizeOrder(value: string): number | null {
  const normalized = value.toLowerCase().trim()

  // Single letter sizes
  if (normalized === 's') return 1
  if (normalized === 'm') return 2
  if (normalized === 'l') return 3

  // XS pattern: xs, xxs, xxxs (each X subtracts 1 from S)
  const xsMatch = normalized.match(/^(x+)s$/)
  if (xsMatch) {
    return 1 - xsMatch[1].length // xs=0, xxs=-1, xxxs=-2
  }

  // XL pattern: xl, xxl, xxxl (each X adds 1 to L)
  const xlMatch = normalized.match(/^(x+)l$/)
  if (xlMatch) {
    return 3 + xlMatch[1].length // xl=4, xxl=5, xxxl=6
  }

  // Numeric XL pattern: 2xl, 3xl, 4xl, etc.
  const numXlMatch = normalized.match(/^(\d+)xl$/)
  if (numXlMatch) {
    return 3 + parseInt(numXlMatch[1], 10) // 2xl=5, 3xl=6, 4xl=7
  }

  return null
}

/**
 * Infers attribute type from attribute name
 */
function inferAttributeType(attrName: string): 'SIZE' | 'COLOR' | 'TEXT' {
  const normalized = attrName.toLowerCase().trim()

  if (normalized === 'talla') return 'SIZE'
  if (normalized === 'color') return 'COLOR'

  return 'TEXT'
}

/**
 * Creates a comparator function for attribute values based on attribute name
 */
export function createAttributeComparator(attrName: string): (a: string, b: string) => number {
  const type = inferAttributeType(attrName)

  return (a: string, b: string): number => {
    if (type === 'SIZE') {
      const aOrder = getSizeOrder(a)
      const bOrder = getSizeOrder(b)

      // Both are standard letter sizes - compare by order
      if (aOrder !== null && bOrder !== null) {
        return aOrder - bOrder
      }

      // One is standard, one is not - standard sizes first
      if (aOrder !== null) return -1
      if (bOrder !== null) return 1

      // Neither is standard (numeric sizes like 32, 34, etc.) - use natural sort
      return naturalSort(a, b)
    }

    // COLOR, TEXT, and others: alphabetical
    return a.localeCompare(b, undefined, { sensitivity: 'base' })
  }
}
